import type { Program, ProgramTimelineContentObject } from 'models';
import {
  ActivityIcon,
  BookmarkFillIcon,
  BookmarkLineIcon,
  Button,
  Dropdown,
  EventIcon,
  Flex,
  More2LineIcon,
  PollIcon,
  PostIcon,
  WithingsOrderPostIcon,
} from 'ui';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- Not re-exported from the features/polls barrel's public surface used elsewhere
import EditPoll from 'features/polls/EditPoll';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- Not re-exported from the features/programs barrel's public surface
import EditActivity from 'features/programs/feed/EditActivity';
import { ProgramEventInviteesModal } from '../../../components';
import { EditPost } from '../../../posts';
import { EditWithingsOrderPost } from '../../../subscribe';
import { UpdateTimelineObject } from '../../../update-timeline-object';
import { TextPreview } from '../text-preview';
import { useProgramTimelineItemHeaderActions } from './hooks';

const getTimelineItemIcon = (type: string) => {
  switch (type) {
    case 'activity':
      return <ActivityIcon size="middle" type="primary" />;
    case 'event':
      return <EventIcon size="middle" type="primary" />;
    case 'poll':
      return <PollIcon size="middle" type="primary" />;
    case 'withings_order_post':
      return <WithingsOrderPostIcon size="middle" type="primary" />;
    default:
      return <PostIcon size="middle" type="primary" />;
  }
};

type P = {
  item: ProgramTimelineContentObject;
  program: Program;
};

/**
 * @deprecated AI-generated code, not reviewed. Needs revision before further use — do not use as a reference or pattern to follow.
 */
const ProgramTimelineItemHeader = ({ item, program }: P) => {
  const {
    actions,
    contextHolder,
    editModal: {
      activityDraft,
      eventDraft,
      isOpen: isEditModalOpen,
      onCancel: handleEditModalCancel,
      onSuccess: handleEditModalSuccess,
      pollDraft,
    },
    inviteesEventId,
    isBookmarking,
    onBookmarkClick,
    onCloseInvitees,
    onMenuAction,
  } = useProgramTimelineItemHeaderActions({ item, program });

  return (
    <>
      {contextHolder}

      <div className="topic-item__header">
        <div className="topic-item__header-icon">{getTimelineItemIcon(item.type)}</div>

        <TextPreview item={item} />

        <Flex gap="xs">
          <Button
            icon={item.bookmarked ? <BookmarkFillIcon /> : <BookmarkLineIcon />}
            loading={isBookmarking}
            onClick={onBookmarkClick}
            type="link-secondary"
          />
          <Dropdown
            menu={{
              items: actions,
              onClick({ key }) {
                onMenuAction(key);
              },
            }}
            trigger={['click']}
          >
            <Button icon={<More2LineIcon />} type="link-secondary" />
          </Dropdown>
        </Flex>
      </div>

      {item.type === 'post' ? (
        <EditPost
          isOpened={isEditModalOpen}
          objectData={item}
          onCancel={handleEditModalCancel}
          onSuccess={handleEditModalSuccess}
          program={program}
        />
      ) : null}

      {item.type === 'activity' && activityDraft ? (
        <EditActivity
          activity={activityDraft}
          isOpened={isEditModalOpen}
          onCancel={handleEditModalCancel}
          // eslint-disable-next-line @typescript-eslint/require-await -- EditActivity's onSuccess prop requires an async function
          onSuccess={async () => {
            handleEditModalSuccess();
          }}
          program={program}
        />
      ) : null}

      {item.type === 'poll' && pollDraft ? (
        <EditPoll
          isOpened={isEditModalOpen}
          onCancel={handleEditModalCancel}
          onSuccess={handleEditModalSuccess}
          poll={pollDraft}
          program={program}
        />
      ) : null}

      {item.type === 'withings_order_post' ? (
        <EditWithingsOrderPost
          isOpened={isEditModalOpen}
          objectData={item}
          onCancel={handleEditModalCancel}
          onSuccess={handleEditModalSuccess}
          program={program}
        />
      ) : null}

      {item.type === 'event' ? (
        <>
          <UpdateTimelineObject
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- The legacy draft shape is not typed
            draft={eventDraft as never}
            isOpenEditorModal={isEditModalOpen}
            onRequestClose={handleEditModalCancel}
            onSubmit={handleEditModalSuccess}
            program={program}
          />
          <ProgramEventInviteesModal eventId={inviteesEventId} onCancel={onCloseInvitees} />
        </>
      ) : null}
    </>
  );
};

export default ProgramTimelineItemHeader;
